import * as React from 'react';
import { Box, Text } from '@radix-ui/themes';

import { GraphQLTable, Column } from '../../components/GraphQLTable';
import {
  GetBlocksDocument,
  GetBlocksQuery,
  GetBlocksQueryVariables,
} from '../../graphql/queries/GetBlocks.generated';
import TimeAgoTooltip from '../../components/TimeAgoTooltip';
import HashShortener from '../../components/HashShortener';
import BlockNumberDisplay from '../../components/BlockNumberDisplay';
import ViewButton from '../../components/ViewButton';

// Helper function to format decimal values
const formatValue = (
  value: string | number | null | undefined,
  currency: string,
): string => {
  if (value === null || value === undefined) return '-';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '-';

  // For ETH, show more decimals if value is small
  if (currency === 'ETH') {
    const decimals = numValue < 1 ? 6 : 4;
    return numValue.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }
  // For USD, show standard 2 decimals with thousand separators
  return numValue.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const BlockList: React.FC = () => {
  const columns: Column<GetBlocksQuery['blocks']['edges'][0]['node']>[] = [
    {
      header: 'Number',
      accessor: 'number',
      sortable: true,
      sortField: 'number',
      filterable: true,
      filterField: 'number',
      filterConfig: {
        type: 'number',
        placeholder: 'Filter by number...',
      },
      render: ({ value }) => (
        <BlockNumberDisplay blockNumber={value} link={`/blocks/${value}`} />
      ),
    },
    {
      header: 'Hash',
      accessor: 'hash',
      render: ({ value }) => <HashShortener hash={value} />,
      filterable: true,
      filterField: 'hash',
      filterConfig: {
        type: 'text',
        placeholder: 'Filter by hash...',
      },
    },
    {
      header: 'Timestamp',
      accessor: 'timestamp',
      sortable: true,
      sortField: 'timestamp',
      filterable: true,
      filterField: 'timestamp',
      filterConfig: {
        type: 'text',
        placeholder: 'Filter by timestamp...',
      },
      render: ({ value }) => <TimeAgoTooltip value={value} />,
    },
    {
      header: 'Transaction Count',
      accessor: 'transactionCount',
      sortable: true,
      sortField: 'transactionCount',
      filterable: true,
      filterField: 'transactionCount',
      filterConfig: {
        type: 'number',
        placeholder: 'Min count...',
      },
    },
    {
      header: 'Value (ETH)',
      accessor: 'valueNative',
      sortable: true,
      sortField: 'valueNative',
      filterable: true,
      filterField: 'valueNative',
      filterConfig: {
        type: 'number',
        placeholder: 'Min ETH value...',
      },
      render: ({ value }) => (
        <Text
          size="2"
          style={{
            fontFamily: 'var(--code-font-family)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formatValue(value, 'ETH')}
        </Text>
      ),
    },
    {
      header: 'Value (USD)',
      accessor: 'valueUsd',
      sortable: true,
      sortField: 'valueUsd',
      filterable: true,
      filterField: 'valueUsd',
      filterConfig: {
        type: 'number',
        placeholder: 'Min USD value...',
      },
      render: ({ value }) => (
        <Text
          size="2"
          style={{
            fontFamily: 'var(--code-font-family)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          ${formatValue(value, 'USD')}
        </Text>
      ),
    },
    {
      header: null,
      render: ({ row }) => (
        <ViewButton href={`/blocks/${row.number}`} />
      ),
    },
  ];

  return (
    <Box style={{ width: '100%', overflowX: 'auto' }}>
      <GraphQLTable<
        GetBlocksQuery,
        GetBlocksQueryVariables,
        'blocks',
        GetBlocksQuery['blocks']['edges'][0]['node']
      >
        query={GetBlocksDocument}
        columns={columns}
        connectionPath="blocks"
        loadingMessage="Fetching latest blocks..."
        errorMessage="Failed to fetch blocks."
        noDataMessage="No blocks available."
      />
    </Box>
  );
};

export default BlockList;
